import { Activity, Calendar, User } from 'lucide-react';
import { useProject } from '@/hooks/useProjects';
import { format } from 'date-fns';

interface ProjectActivitiesProps {
  projectId: string;
  limit?: number;
}

export default function ProjectActivities({ projectId, limit }: ProjectActivitiesProps) {
  const { data: project, isLoading } = useProject(projectId);
  
  const activities = project?.recent_activities || [];
  const displayedActivities = limit ? activities.slice(0, limit) : activities;

  const getActivityIcon = (type: string) => {
    return <Activity className="h-4 w-4 text-blue-500" />;
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="animate-pulse">
            <div className="h-16 bg-gray-200 rounded"></div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-medium">
        {limit ? 'Recent Activity' : 'Project Activity Log'}
      </h3>

      {displayedActivities && displayedActivities.length > 0 ? (
        <div className="space-y-4">
          {displayedActivities.map((activity) => (
            <div key={activity.id} className="flex items-start gap-3 p-3 border rounded-lg">
              <div className="w-8 h-8 bg-blue-50 rounded-full flex items-center justify-center">
                {getActivityIcon(activity.activity_type)}
              </div>
              
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium">{activity.title}</h4>
                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Calendar className="h-3 w-3" />
                    {format(new Date(activity.activity_date), 'MMM d, h:mm a')}
                  </div>
                </div>
                
                {activity.description && (
                  <p className="text-sm text-muted-foreground mt-1">
                    {activity.description}
                  </p>
                )}
                
                {activity.performed_by_user && (
                  <div className="flex items-center gap-1 mt-2 text-xs text-muted-foreground">
                    <User className="h-3 w-3" />
                    {activity.performed_by_user.first_name} {activity.performed_by_user.last_name}
                  </div>
                )}
                
                <div className="flex items-center gap-4 mt-2">
                  {activity.related_milestone && (
                    <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                      Milestone: {activity.related_milestone.milestone_name}
                    </span>
                  )}
                  
                  {activity.related_expense && (
                    <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                      Expense: ${activity.related_expense.amount}
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
          
          {limit && activities.length > limit && (
            <div className="text-center">
              <p className="text-sm text-muted-foreground">
                View all {activities.length} activities in the Activities tab
              </p>
            </div>
          )}
        </div>
      ) : (
        <div className="text-center py-8">
          <Activity className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium mb-2">No Activities</h3>
          <p className="text-sm text-muted-foreground">
            Project activities will appear here as work progresses.
          </p>
        </div>
      )}
    </div>
  );
}